import {
  ButtonBase,
  Stack,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  Divider,
  IconButton,
  Box,
  Tooltip,
} from "@mui/material";
import React, { createContext, useContext, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DarkModeOutlined from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import {
  useGetIdentity,
  useLogout,
  useTranslation,
  useSetLocale,
  useGetLocale,
  useInvalidate,
} from "@refinedev/core";
import { KeycloakIdentity } from "../../providers/authProvider";
import { ColorModeContext } from "../../providers/colorProvider";
import { setAxiosLanguage } from "../../utils/axios";

// Lets entries passed into the menu close it without prop drilling.
const AccountMenuContext = createContext<() => void>(() => {});

export const useCloseAccountMenu = () => useContext(AccountMenuContext);

interface AccountMenuProps {
  menuId: string;
  // Rendered in the toolbar row before the theme toggle.
  toolbarStart?: React.ReactNode;
  // Rendered in the toolbar row between the theme and language toggles.
  toolbarMiddle?: React.ReactNode;
  // Rendered between the divider and the logout item.
  menuItems?: React.ReactNode;
}

// Shared account menu shell used by the seller UserMenu and the AdminUserMenu.
export const AccountMenu = ({
  menuId,
  toolbarStart,
  toolbarMiddle,
  menuItems,
}: AccountMenuProps) => {
  const { data: user } = useGetIdentity<KeycloakIdentity>();
  const { translate } = useTranslation();
  const { mode, toggleMode } = useContext(ColorModeContext);
  const changeLanguage = useSetLocale();
  const locale = useGetLocale();
  const currentLocale = locale();
  const invalidate = useInvalidate();
  const { mutate: logout } = useLogout();

  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorElement);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  const handleChangeLanguage = (lang: string) => {
    setAxiosLanguage(lang);
    changeLanguage(lang);
    void invalidate({ resource: "all", invalidates: ["all"] });
  };

  const nextLocale = currentLocale === "tr" ? "en" : "tr";

  if (!user) {
    return null;
  }

  const initials = (user.name?.[0] ?? "") + (user.surname?.[0] ?? "");

  return (
    <AccountMenuContext.Provider value={handleClose}>
      <ButtonBase
        TouchRippleProps={{ style: { borderRadius: "8px", margin: "-4px" } }}
        onClick={handleOpen}
        sx={{ width: "100%", borderRadius: "8px", p: 0.5 }}
      >
        <Stack
          direction="row"
          gap="8px"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <Avatar
            alt={`${user.name ?? ""} ${user.surname ?? ""}`}
            sx={{ width: 32, height: 32, fontSize: 16, flexShrink: 0 }}
            variant="rounded"
          >
            {initials}
          </Avatar>
          <Typography
            variant="subtitle2"
            noWrap
            sx={{
              flex: 1,
              minWidth: 0,
              textAlign: "left",
              display: { xs: "none", sm: "inline-block" },
            }}
          >
            {translate("pages.layout.welcomeMessage", { name: user.name })}
          </Typography>
          <KeyboardArrowDownIcon sx={{ flexShrink: 0 }} />
        </Stack>
      </ButtonBase>

      <Menu
        anchorEl={anchorElement}
        id={menuId}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "left", vertical: "bottom" }}
        anchorOrigin={{ horizontal: "left", vertical: "top" }}
        MenuListProps={{ dense: true }}
        sx={{
          mt: 1,
          "& .MuiPaper-root": { minWidth: 220 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 0.5,
            py: 0.75,
            mx: 1,
            my: 0.5,
            borderRadius: 1,
            bgcolor: "action.hover",
          }}
        >
          {toolbarStart}

          <Tooltip title={mode === "dark" ? "Açık tema" : "Koyu tema"}>
            <IconButton
              onClick={toggleMode}
              color="inherit"
              sx={{ width: 34, height: 34 }}
            >
              {mode === "dark" ? (
                <LightModeOutlined fontSize="small" />
              ) : (
                <DarkModeOutlined fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          {toolbarMiddle}

          <Tooltip
            title={nextLocale === "en" ? "Switch to English" : "Türkçe'ye geç"}
          >
            <IconButton
              color="inherit"
              onClick={() => handleChangeLanguage(nextLocale)}
              sx={{ width: 34, height: 34 }}
            >
              <Typography fontSize="1rem" fontWeight={700} lineHeight={1}>
                {(currentLocale ?? "tr").toUpperCase()}
              </Typography>
            </IconButton>
          </Tooltip>
        </Box>

        <Divider />

        {menuItems}

        <MenuItem
          onClick={() => {
            logout();
            handleClose();
          }}
        >
          {translate("pages.layout.logout")}
        </MenuItem>
      </Menu>
    </AccountMenuContext.Provider>
  );
};
